/**
 * Logical subgrid contract.
 *
 * 4x4 remains a comparison candidate. COL-003B freezes 5x5 as the visible
 * terrain foundation (`FOUNDATION_DIVISION`) while placement stays on legacy
 * `slotId` centers. Runtime import is limited to that foundation seam.
 */

export const LEGACY_CELL_SIZE = 2.4;

/** Must stay aligned with `gridToWorld` in `src/migration/mapParkState.ts`. */
export const LEGACY_GRID_ORIGIN_COL = 2;
export const LEGACY_GRID_ORIGIN_ROW = 1.5;

export const CANDIDATE_DIVISIONS = [4, 5] as const;
export type CandidateDivision = (typeof CANDIDATE_DIVISIONS)[number];

/** Frozen by `docs/decisions/SUBGRID_5X5_FOUNDATION.md` for the COL-003B terrain slice. */
export const FOUNDATION_DIVISION = 5;

export type QuantizeMode = 'nearest' | 'ceil' | 'floor';

export interface WorldPoint {
  x: number;
  z: number;
}

export interface GridCoord {
  col: number;
  row: number;
}

export interface SubgridCoord {
  sx: number;
  sz: number;
}

export interface SubgridSpec {
  division: number;
  legacyCellSize: number;
  subcellSize: number;
}

export interface LegacyCenterCompatibility {
  exactSubcellCenter: boolean;
  exactSubcellVertex: boolean;
  nearestSubcellCenterOffset: number;
}

export interface LengthQuantization {
  cells: number;
  snapped: number;
  error: number;
}

export interface FootprintCoverage {
  cellsX: number;
  cellsZ: number;
  coveredWidth: number;
  coveredDepth: number;
  leftoverX: number;
  leftoverZ: number;
}

export function assertPositiveIntegerDivision(division: number): void {
  if (!Number.isInteger(division) || division < 1) {
    throw new Error(`Subgrid division must be a positive integer, got ${division}`);
  }
}

export function subcellSize(legacyCellSize: number, division: number): number {
  assertPositiveIntegerDivision(division);
  if (!(legacyCellSize > 0)) {
    throw new Error(`Legacy cell size must be positive, got ${legacyCellSize}`);
  }
  return legacyCellSize / division;
}

export function createSubgridSpec(division: number, legacyCellSize = LEGACY_CELL_SIZE): SubgridSpec {
  return {
    division,
    legacyCellSize,
    subcellSize: subcellSize(legacyCellSize, division),
  };
}

export function legacyGridToWorld(col: number, row: number, cellSize = LEGACY_CELL_SIZE): WorldPoint {
  return {
    x: (col - LEGACY_GRID_ORIGIN_COL) * cellSize,
    z: (row - LEGACY_GRID_ORIGIN_ROW) * cellSize,
  };
}

export function worldToLegacyGrid(x: number, z: number, cellSize = LEGACY_CELL_SIZE): { col: number; row: number } {
  return {
    col: x / cellSize + LEGACY_GRID_ORIGIN_COL,
    row: z / cellSize + LEGACY_GRID_ORIGIN_ROW,
  };
}

export function legacyCellMinCorner(col: number, row: number, cellSize = LEGACY_CELL_SIZE): WorldPoint {
  const center = legacyGridToWorld(col, row, cellSize);
  const half = cellSize / 2;
  return { x: center.x - half, z: center.z - half };
}

/** World min-corner of legacy cell (0, 0); global subcell (0, 0) starts here. */
export function subgridWorldOrigin(cellSize = LEGACY_CELL_SIZE): WorldPoint {
  return legacyCellMinCorner(0, 0, cellSize);
}

export function worldToSubgrid(x: number, z: number, spec: SubgridSpec): SubgridCoord {
  const origin = subgridWorldOrigin(spec.legacyCellSize);
  return {
    sx: Math.floor((x - origin.x) / spec.subcellSize),
    sz: Math.floor((z - origin.z) / spec.subcellSize),
  };
}

export function subcellMinCorner(coord: SubgridCoord, spec: SubgridSpec): WorldPoint {
  const origin = subgridWorldOrigin(spec.legacyCellSize);
  return {
    x: origin.x + coord.sx * spec.subcellSize,
    z: origin.z + coord.sz * spec.subcellSize,
  };
}

export function subcellWorldCenter(coord: SubgridCoord, spec: SubgridSpec): WorldPoint {
  const min = subcellMinCorner(coord, spec);
  const half = spec.subcellSize / 2;
  return { x: min.x + half, z: min.z + half };
}

export function legacyCellToSubgridOrigin(col: number, row: number, spec: SubgridSpec): SubgridCoord {
  return {
    sx: col * spec.division,
    sz: row * spec.division,
  };
}

/** All logical subcell centers inside one legacy cell, local X then Z. */
export function legacyCellSubcellCenters(col: number, row: number, spec: SubgridSpec): WorldPoint[] {
  const world = legacyGridToWorld(col, row, spec.legacyCellSize);
  const mid = (spec.division - 1) / 2;
  const centers: WorldPoint[] = [];
  for (let localZ = 0; localZ < spec.division; localZ += 1) {
    for (let localX = 0; localX < spec.division; localX += 1) {
      centers.push({
        x: world.x + (localX - mid) * spec.subcellSize,
        z: world.z + (localZ - mid) * spec.subcellSize,
      });
    }
  }
  return centers;
}

export function localSubcellCenter(localX: number, localZ: number, spec: SubgridSpec): WorldPoint {
  return {
    x: (localX + 0.5) * spec.subcellSize,
    z: (localZ + 0.5) * spec.subcellSize,
  };
}

export function legacyCenterCompatibility(spec: SubgridSpec): LegacyCenterCompatibility {
  const even = spec.division % 2 === 0;
  const nearestOffset = even ? Math.hypot(spec.subcellSize / 2, spec.subcellSize / 2) : 0;
  return {
    exactSubcellCenter: !even,
    exactSubcellVertex: even,
    nearestSubcellCenterOffset: nearestOffset,
  };
}

/**
 * Exact legacy slot/path world center, plus a deterministic nearest subcell.
 * For odd divisions the nearest subcell is the center cell. For even divisions
 * the center sits on a 4-cell vertex; this picks the +x/+z quadrant.
 */
export function mapLegacyCenter(col: number, row: number, spec: SubgridSpec): {
  world: WorldPoint;
  nearestSubcell: SubgridCoord;
  nearestSubcellWorld: WorldPoint;
} {
  const world = legacyGridToWorld(col, row, spec.legacyCellSize);
  const origin = legacyCellToSubgridOrigin(col, row, spec);
  const local = Math.floor(spec.division / 2);
  const nearestSubcell = { sx: origin.sx + local, sz: origin.sz + local };
  return {
    world,
    nearestSubcell,
    nearestSubcellWorld:
      spec.division % 2 === 1 ? world : subcellWorldCenter(nearestSubcell, spec),
  };
}

export function quantizeLength(length: number, spec: SubgridSpec, mode: QuantizeMode = 'ceil'): LengthQuantization {
  if (!(length >= 0)) {
    throw new Error(`Length must be non-negative, got ${length}`);
  }
  const raw = length / spec.subcellSize;
  const cells =
    mode === 'ceil' ? Math.ceil(raw - 1e-12) : mode === 'floor' ? Math.floor(raw + 1e-12) : Math.round(raw);
  const snapped = cells * spec.subcellSize;
  return { cells, snapped, error: snapped - length };
}

export function footprintCoverCells(width: number, depth: number, spec: SubgridSpec): FootprintCoverage {
  const x = quantizeLength(width, spec, 'ceil');
  const z = quantizeLength(depth, spec, 'ceil');
  return {
    cellsX: x.cells,
    cellsZ: z.cells,
    coveredWidth: x.snapped,
    coveredDepth: z.snapped,
    leftoverX: x.error,
    leftoverZ: z.error,
  };
}

export function logicalCellGrowth(legacyCellCount: number, division: number): number {
  assertPositiveIntegerDivision(division);
  return legacyCellCount * division * division;
}
